import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Box } from '~/eds/src/components/Box/Box';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { Text } from '~/eds/src/components/Text/Text';
import { HtmlEntityType } from '~/eds/src/enums/HtmlEntityType';
import { api } from '~/redux/api/createApi';
import { DepartmentTree } from '~/types/Department';
import { MODAL_DELETE_DEPARTMENT, MODAL_NAME } from '~/types/modal.types';
import { ERROR } from '~/types/toast.types';

type Props = {
  clientId: number;
  item: DepartmentTree;
  handleShowModal: (
    modalName: string,
    item: DepartmentTree,
    isChildren?: boolean,
  ) => void;
  onClick: (item: DepartmentTree) => void;
};

const DepartmentItem = ({
  clientId,
  item,
  handleShowModal,
  onClick,
}: Props) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState<boolean>(false);
  const [getDepartmentChildren, { data, isFetching, isError, isSuccess }] =
    api.endpoints.getDepartmentChildren.useLazyQuery();

  useEffect(() => {
    if (isSuccess && !isFetching) {
      setExpanded(true);
    }
  }, [isSuccess, isFetching]);

  useEffect(() => {
    if (isError && !isFetching) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DepartmentItem.errorMessage',
          defaultMessage: 'Something went wrong, please try again later.',
          description: 'Error message when department children fetch fails',
        }),
      );
    }
  }, [isError, isFetching]);

  useEffect(() => {
    if (expanded) {
      getDepartmentChildren({ departmentId: item.department_id, clientId });
    }
  }, [item.children_count]);

  const getIcon = () => {
    if (isFetching) return 'loading';

    if (expanded) return 'chevron-down';

    return 'chevron-right';
  };

  const toggleChildren = () => {
    if (expanded) {
      setExpanded(false);
    } else {
      if (!isFetching) {
        getDepartmentChildren({ departmentId: item.department_id, clientId });
      }
    }
  };

  const getMenuItems = () => {
    const items = [];

    items.push({
      id: '1',
      content: intl.formatMessage({
        id: 'DepartmentItem.addSubdepartment',
        defaultMessage: 'Add Subdepartment',
        description: 'Menu item to add a subdepartment',
      }),
      onClick: () => handleShowModal(MODAL_NAME, item, true),
    });

    items.push({
      id: '2',
      content: intl.formatMessage({
        id: 'DepartmentItem.rename',
        defaultMessage: 'Rename',
        description: 'Menu item to rename department',
      }),
      onClick: () => handleShowModal(MODAL_NAME, item),
    });

    items.push({
      id: '3',
      content: intl.formatMessage({
        id: 'DepartmentItem.delete',
        defaultMessage: 'Delete',
        description: 'Menu item to delete department',
      }),
      onClick: () => handleShowModal(MODAL_DELETE_DEPARTMENT, item),
    });

    return items;
  };

  const renderChildren = () => {
    return (
      <Box>
        {data?.results.map((child) => (
          <DepartmentItem
            onClick={onClick}
            key={`child_department_${child.department_id}`}
            clientId={clientId}
            item={child}
            handleShowModal={handleShowModal}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box pt={3} pl={2}>
      <Layout
        direction="row"
        justify="space-between"
        align="center"
        borderBottom="border"
        pb={3}
      >
        <Box>
          <Layout direction="row" spacing={2}>
            {item.children_count ? (
              <IconButton
                onClick={toggleChildren}
                icon={getIcon()}
                text={intl.formatMessage(
                  {
                    id: 'DepartmentItem.toggleSubdepartments',
                    defaultMessage:
                      'sub-departments of department {departmentName}',
                    description:
                      'Toggle subdepartments accessibility text. Example: departmentName = "Legal Department"',
                  },
                  { departmentName: item.department_name },
                )}
              />
            ) : null}
            <Link
              variant="subtitle"
              onClick={() => onClick(item)}
              color="text.primary"
            >
              {item.department_name}
            </Link>
          </Layout>
          <Box>
            {item.members_count ? (
              <>
                <Text variant="tiny" color="text.quiet" mr={2}>
                  {intl.formatMessage(
                    {
                      id: 'DepartmentItem.membersCount',
                      defaultMessage:
                        '{count, plural, one {# user} other {# users}}',
                      description: 'Number of users in the department',
                    },
                    { count: item.members_count },
                  )}
                </Text>
                <Text>{HtmlEntityType.Bullet}</Text>
              </>
            ) : null}
            {item.children_count ? (
              <Text variant="tiny" color="text.quiet" ml={2}>
                {intl.formatMessage(
                  {
                    id: 'DepartmentItem.childrenCount',
                    defaultMessage:
                      '{count, plural, one {# subdepartment} other {# subdepartments}}',
                    description: 'Number of subdepartments in the department',
                  },
                  { count: item.children_count },
                )}
              </Text>
            ) : null}
          </Box>
        </Box>
        <Box>
          <ActionsMenu
            id={`client_department_list_actions_menu?id=${item.department_id}`}
            items={getMenuItems()}
            align="end"
            icon={undefined}
            smallIcon={undefined}
            title={intl.formatMessage({
              id: 'DepartmentItem.editDepartmentMenu',
              defaultMessage: 'edit department menu',
              description: 'Actions menu title for department operations',
            })}
          />
        </Box>
      </Layout>
      {expanded && renderChildren()}
    </Box>
  );
};

export default DepartmentItem;
